import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Pagination,
  Paper,
  TextField,
  Chip,
  CircularProgress,
  InputAdornment,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import Groups2Icon from "@mui/icons-material/Groups2";
import DashboardIcon from "@mui/icons-material/Dashboard";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import BlockIcon from "@mui/icons-material/Block";
import BarChartIcon from "@mui/icons-material/BarChart";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { useParams, Outlet, useLocation } from "react-router-dom";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

import {
  fetchAllGateVisitors,
  fetchGateVisitorStatuses,
  fetchGateVisitorTypes,
} from "./Utils/gateVisitors.api";
import { getStatusInfo } from "./Utils/gateVisitors.utils";
import GateVisitorsCards from "./gateVisitorsCards";
import GateVisitorsStatusChips from "./StatusFilterCards";
import GateDashboard from "./GateDashboard";
import GatePreAuthPage from "./GatePreAuthPage";
import GateBlacklistPage from "./GateBlacklistPage";
import GateReportsPage from "./GateReportsPage";
import api from "../../utils/api";

const GateVisitorsPage = () => {
  const { id } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState(0);

  const [visitors, setVisitors] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [types, setTypes] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatuses, setFilterStatuses] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [page, setPage] = useState(1);
  const [selectedVisitorTypes, setSelectedVisitorTypes] = useState([]);
  const [smartFilterEnabled, setSmartFilterEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  const itemsPerPage = 18;

  const getVisitorTypeName = (typeId) => {
    const found = types.find((t) => Number(t.TypeID) === Number(typeId));
    return found?.TypeName || "Unknown Type";
  };

  const applyFilters = () => {
    let results = [...visitors];

    const terms = searchTerm
      .toLowerCase()
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    if (terms.length) {
      results = results.filter((v) => {
        const typeName = getVisitorTypeName(v.VisitorTypeID).toLowerCase();
        const statusText = (v.StatusName || "Unknown").toLowerCase();
        const visitorName = `${v.FirstName || ""} ${v.LastName || ""}`.toLowerCase();
        const memberName = `${v.MemberFirstName || ""} ${v.MemberLastName || ""}`.toLowerCase();
        const memberAddress = (v.MemberAddress || "").toLowerCase();
        const combined = `${v.VisitID} ${v.ClubMemberID} ${typeName} ${statusText} ${visitorName} ${memberName} ${memberAddress}`;
        return terms.some((term) => combined.includes(term));
      });
    }

    if (filterStatuses.length) {
      results = results.filter((v) =>
        filterStatuses.includes(v.StatusName || "Unknown")
      );
    }

    if (startDate || endDate) {
      results = results.filter((v) => {
        const visitDate = new Date(v.ArrivalDate);
        if (startDate && visitDate < new Date(startDate)) return false;
        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (visitDate > end) return false;
        }
        return true;
      });
    }

    if (selectedVisitorTypes.length) {
      results = results.filter((v) =>
        selectedVisitorTypes.includes(v.VisitorTypeID)
      );
    }

    if (smartFilterEnabled) {
      const today = new Date().toDateString();
      results = results.filter((v) => {
        const isToday = new Date(v.ArrivalDate).toDateString() === today;
        const isUnknown = (v.StatusName || "Unknown").toLowerCase() === "unknown";
        return isToday && isUnknown;
      });
    }

    setFiltered(results);
  };

  const loadData = async () => {
    setLoading(true);

    try {
      const [resVisitors, resStatuses, resTypes] = await Promise.all([
        fetchAllGateVisitors(),
        fetchGateVisitorStatuses(),
        fetchGateVisitorTypes(),
      ]);

      const allVisitors = resVisitors?.data?.data || resVisitors?.data || [];
      const allStatuses = resStatuses?.data || [];
      const allTypes = resTypes || [];

      const memberIDs = [
        ...new Set(allVisitors.map((v) => v.ClubMemberID).filter(Boolean)),
      ];

      const memberMap = {};

      await Promise.all(
        memberIDs.map(async (memberId) => {
          try {
            const res = await api.get(`/members/${encodeURIComponent(memberId)}`);
            memberMap[memberId] = res.data;
          } catch (err) {
            console.error(`Error fetching member info for ID ${memberId}`, err);
          }
        })
      );

      const enrichedVisitors = allVisitors.map((v) => {
        const m = memberMap[v.ClubMemberID] || {};
        return {
          ...v,
          MemberFirstName: m.FirstName || "",
          MemberLastName: m.LastName || "",
          MemberAddress: m.FullAddress || "",
        };
      });

      setVisitors(enrichedVisitors);
      setStatuses(allStatuses);
      setTypes(allTypes);

      const counts = {};
      for (const v of enrichedVisitors) {
        const s = (v.StatusName || "Unknown").toLowerCase();
        counts[s] = (counts[s] || 0) + 1;
      }
      setStatusCounts(counts);
    } catch (error) {
      console.error("Error loading gate visitor data:", error);
      setFetchError("Failed to load visitor data. Please refresh.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (location?.state?.reload) {
      loadData();
      window.history.replaceState({}, document.title);
    }
  }, [location?.state?.reload]);

  useEffect(() => {
    applyFilters();
  }, [
    searchTerm,
    filterStatuses,
    startDate,
    endDate,
    visitors,
    selectedVisitorTypes,
    smartFilterEnabled,
  ]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterStatuses, startDate, endDate, selectedVisitorTypes, smartFilterEnabled]);

  const handleResetFilters = () => {
    setSearchTerm("");
    setFilterStatuses([]);
    setStartDate(null);
    setEndDate(null);
    setSelectedVisitorTypes([]);
    setSmartFilterEnabled(false);
    setPage(1);
  };

  const handleStatusToggle = (status) => {
    setFilterStatuses((prev) =>
      prev.includes(status)
        ? prev.filter((s) => s !== status)
        : [...prev, status]
    );
  };

  const handleVisitorTypeToggle = (typeId) => {
    setSelectedVisitorTypes((prev) =>
      prev.includes(typeId)
        ? prev.filter((id) => id !== typeId)
        : [...prev, typeId]
    );
  };

const paginated = filtered.slice((page - 1) * itemsPerPage, page * itemsPerPage);
const totalPages = Math.max(1, Math.ceil(filtered.length / itemsPerPage));

const todayUnknownCount = useMemo(() => {
  return visitors.filter((v) => {
    const isToday =
      new Date(v.ArrivalDate).toDateString() === new Date().toDateString();
    const isUnknown =
      (v.StatusName || "Unknown").toLowerCase() === "unknown";
    return isToday && isUnknown;
  }).length;
}, [visitors]);

if (id) return <Outlet />;

  const pageCardSx = {
    borderRadius: "18px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
    backgroundColor: "rgba(255,255,255,0.96)",
  };

  const sectionCardSx = {
    borderRadius: "16px",
    border: "1px solid rgba(47,99,173,0.08)",
    boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
    backgroundColor: "rgba(255,255,255,0.96)",
    p: { xs: 2, md: 2.5 },
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "rgba(255,255,255,0.96)",
      borderRadius: "10px",
    },
  };

  const primaryButtonSx = {
    backgroundColor: "#2f63ad",
    color: "white",
    fontWeight: 700,
    borderRadius: "10px",
    textTransform: "none",
    px: 2.25,
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#1f4f94",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "#90caf9",
      color: "#fff",
    },
  };

  const secondaryButtonSx = {
    borderRadius: "10px",
    textTransform: "none",
    fontWeight: 700,
    borderColor: "rgba(47,99,173,0.18)",
    color: "#223142",
    px: 2.25,
    "&:hover": {
      borderColor: "#2f63ad",
      color: "#2f63ad",
      backgroundColor: "rgba(47,99,173,0.04)",
    },
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, width: "100%", minHeight: "100%", boxSizing: "border-box" }}>
      {fetchError && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{fetchError}</Alert>}

      {/* Page header */}
      <Box sx={{ display:"flex", alignItems:"center", gap:1.25, mb:2 }}>
        <Box sx={{ width:40, height:40, minWidth:40, borderRadius:"12px", display:"flex", alignItems:"center", justifyContent:"center", backgroundColor:"rgba(47,99,173,0.08)" }}>
          <Groups2Icon sx={{ color:"#2f63ad" }} />
        </Box>
        <Box>
          <Typography variant="h5" sx={{ fontWeight:700, color:"#223142" }}>Gate Security</Typography>
          <Typography variant="body2" sx={{ color:"#6b778c" }}>Visitor log, pre-authorization, blacklist, dashboard, and reports.</Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper elevation={0} sx={{ borderRadius:"14px", border:"1px solid rgba(47,99,173,0.08)", mb:3, overflow:"hidden" }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
          sx={{ px:1, "& .MuiTab-root":{ textTransform:"none", fontWeight:600, minHeight:48 } }}>
          <Tab icon={<Groups2Icon fontSize="small" />} iconPosition="start" label="Visitor Log" />
          <Tab icon={<DashboardIcon fontSize="small" />} iconPosition="start" label="Dashboard" />
          <Tab icon={<HowToRegIcon fontSize="small" />} iconPosition="start" label="Pre-Authorization" />
          <Tab icon={<BlockIcon fontSize="small" />} iconPosition="start" label="Blacklist" />
          <Tab icon={<BarChartIcon fontSize="small" />} iconPosition="start" label="Reports" />
        </Tabs>
      </Paper>

      {/* Dashboard */}
      {activeTab === 1 && <GateDashboard />}
      {activeTab === 2 && <GatePreAuthPage />}
      {activeTab === 3 && <GateBlacklistPage />}
      {activeTab === 4 && <GateReportsPage />}

      {/* Visitor Log (tab 0) */}
      {activeTab === 0 && (<>


      <Paper elevation={0} sx={{ ...pageCardSx, p: { xs: 2, md: 3 }, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <TextField
            fullWidth
            placeholder="Search by ID, visitor, member, address, or type..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              ...fieldSx,
              maxWidth: { xs: "100%", md: 760 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" sx={{ color: "#6b778c" }} />
                </InputAdornment>
              ),
            }}
          />

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading}
              sx={primaryButtonSx}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </Button>

            <Button
              variant="outlined"
              startIcon={<ViewModuleIcon />}
              disabled
              sx={secondaryButtonSx}
            >
              Card View
            </Button>

            <Button
              variant="outlined"
              startIcon={<RestartAltIcon />}
              onClick={handleResetFilters}
              sx={secondaryButtonSx}
            >
              Reset Filters
            </Button>
          </Box>
        </Box>

        <Box
          sx={{
            mt: 2,
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: 2,
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(d) => setStartDate(d)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: fieldSx,
                },
              }}
            />

            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(d) => setEndDate(d)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  sx: fieldSx,
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Box
          sx={{
            mt: 2,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          <Chip
            label={`Showing ${filtered.length} visitors`}
            variant="outlined"
            sx={{
              borderRadius: "999px",
              fontWeight: 700,
              borderColor: "rgba(47,99,173,0.15)",
              backgroundColor: "rgba(47,99,173,0.04)",
              color: "#223142",
            }}
          />
          <Chip
            label={`Total loaded ${visitors.length}`}
            variant="outlined"
            sx={{
              borderRadius: "999px",
              fontWeight: 700,
            }}
          />
          <Chip
            label={`Today unknown ${todayUnknownCount}`}
            variant="outlined"
            sx={{
              borderRadius: "999px",
              fontWeight: 700,
            }}
          />
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ ...sectionCardSx, mb: 3 }}>
        <GateVisitorsStatusChips
          statuses={statuses}
          statusCounts={statusCounts}
          activeFilters={filterStatuses}
          onToggle={handleStatusToggle}
          visitorTypes={types}
          selectedVisitorTypes={selectedVisitorTypes}
          onVisitorTypeToggle={handleVisitorTypeToggle}
          smartFilterEnabled={smartFilterEnabled}
          onToggleSmartFilter={() => setSmartFilterEnabled((prev) => !prev)}
        />
      </Paper>

      <Paper
        elevation={0}
        sx={{
          ...sectionCardSx,
          p: 0,
          overflow: "hidden",
        }}
      >
        {loading ? (
          <Box
            sx={{
              minHeight: 280,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : filtered.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Groups2Icon sx={{ fontSize: 42, color: "#9aa6b2", mb: 1 }} />
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#223142" }}>
              No gate visitors found
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c", mt: 0.75 }}>
              Try adjusting your search, date range, or selected filters.
            </Typography>
          </Box>
        ) : (
          <Box sx={{ p: 2 }}>
            <GateVisitorsCards
              visits={paginated}
              getStatusDetails={(status) => getStatusInfo(statuses, status)}
              onRefresh={loadData}
            />
          </Box>
        )}
      </Paper>

      {filtered.length > itemsPerPage && (
        <Paper elevation={0} sx={{ ...sectionCardSx, mt:3, display:"flex", alignItems:"center", justifyContent:"space-between", gap:2, flexWrap:"wrap" }}>
          <Typography variant="body2" sx={{ color:"text.secondary" }}>
            Page <strong>{page}</strong> of <strong>{totalPages}</strong>
          </Typography>
          <Pagination count={totalPages} page={page} onChange={(e, val) => setPage(val)} color="primary" shape="rounded" />
        </Paper>
      )}
      </>)}
    </Box>
  );
};

export default GateVisitorsPage;